import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  HiChevronDown,
  HiOutlineAdjustmentsHorizontal,
  HiOutlineHeart,
  HiOutlineXMark,
} from "react-icons/hi2";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import "../css/shop.scss";

const PAGE_SIZE = 12;

const fallbackImage = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 1200">
    <rect width="900" height="1200" fill="#e9e7e0"/>
    <path d="M330 410h240v380H330z" fill="#d2d0c8"/>
    <text x="450" y="850" text-anchor="middle" font-family="Arial" font-size="24" fill="#555">IMAGE COMING SOON</text>
  </svg>
`)}`;

const formatPrice = (price) =>
  new Intl.NumberFormat(siteConfig.locale, {
    style: "currency",
    currency: siteConfig.currency.code,
    maximumFractionDigits: 0,
  }).format(price);

const titleCase = (value) =>
  value
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .replace("T Shirts", "T-Shirts");

const getValues = (searchParams, key) =>
  (searchParams.get(key) || "")
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

const categoryOptions = [...new Set(productData.map((item) => item.category))].map(
  (value) => ({ value, label: titleCase(value) })
);

const sizeOptions = [
  "XS",
  "S",
  "M",
  "L",
  "XL",
  "XXL",
  "25",
  "26",
  "27",
  "28",
  "29",
  "30",
  "31",
  "32",
  "33",
  "34",
  "36",
].map((value) => ({ value: value.toLowerCase(), label: value }));

const colorOptions = [
  { value: "blue", label: "Blue", color: "#526d88" },
  { value: "black", label: "Black", color: "#222222" },
  { value: "white", label: "White", color: "#e9e5da" },
  { value: "grey", label: "Grey", color: "#969692" },
  { value: "beige", label: "Beige", color: "#b8aa91" },
  { value: "green", label: "Green", color: "#596653" },
  { value: "red", label: "Red", color: "#984a50" },
];

const materialOptions = [
  { value: "organic cotton", label: "Organic cotton" },
  { value: "regenerative cotton", label: "Regenerative cotton" },
  { value: "recycled", label: "Recycled fibers" },
  { value: "linen", label: "Linen" },
  { value: "lyocell", label: "TENCEL™ Lyocell" },
];

const colorPatterns = {
  blue: /blue|indigo|cobalt|rinse/,
  black: /black|charcoal|graphite|midnight|ink navy|dark navy/,
  white: /white|ecru|oat|cream/,
  grey: /grey|gray|melange/,
  beige: /sand|khaki|stone|clay|rust|warm sand/,
  green: /olive|sage|forest|pine/,
  red: /raspberry|red/,
};

const categoryMatches = (productCategory, requestedCategory) => {
  if (productCategory === requestedCategory) return true;
  if (requestedCategory === "tops") return productCategory === "t-shirts";
  if (requestedCategory === "sweats") {
    return ["sweatshirts", "sweatpants"].includes(productCategory);
  }
  return false;
};

const FilterDropdown = ({ label, options, selectedValues, onToggle, color }) => (
  <details className="catalog-filter">
    <summary>
      <span>{label}</span>
      {selectedValues.length > 0 && <em>{selectedValues.length}</em>}
      <HiChevronDown aria-hidden="true" />
    </summary>
    <div className="catalog-filter__menu">
      {options.map((option) => (
        <label key={option.value}>
          <input
            type="checkbox"
            checked={selectedValues.includes(option.value)}
            onChange={() => onToggle(option.value)}
          />
          <span className="catalog-filter__check" aria-hidden="true" />
          {color && (
            <span
              className="catalog-filter__color"
              style={{ backgroundColor: option.color }}
              aria-hidden="true"
            />
          )}
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  </details>
);

const CatalogCard = ({ product, favorite, onToggleWishlist }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageUnavailable, setImageUnavailable] = useState(false);

  const handleImageError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = fallbackImage;
    setImageUnavailable(true);
    setImageLoaded(true);
  };

  return (
    <article className="catalog-card">
      <div
        className={`catalog-card__media ${imageLoaded ? "is-loaded" : "is-loading"}`}
      >
        {!imageLoaded && <span className="catalog-card__image-skeleton" />}
        <Link to={`/shopDetails/${product.id}`} aria-label={product.name}>
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={handleImageError}
          />
        </Link>
        {product.badge && <span className="catalog-card__badge">{product.badge}</span>}
        <button
          type="button"
          className={`catalog-card__wishlist ${favorite ? "is-active" : ""}`}
          aria-label={`${favorite ? "Remove" : "Add"} ${product.name} ${
            favorite ? "from" : "to"
          } wishlist`}
          aria-pressed={favorite}
          onClick={() => onToggleWishlist(product.id)}
        >
          <HiOutlineHeart aria-hidden="true" />
        </button>
        {imageUnavailable && (
          <span className="catalog-card__image-status">Preview unavailable</span>
        )}
      </div>
      <div className="catalog-card__copy">
        <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
        <span>{formatPrice(product.price)}</span>
        <small>{product.colors.join(" · ")}</small>
      </div>
    </article>
  );
};

const CatalogSkeleton = () => (
  <article className="catalog-card catalog-card--skeleton" aria-hidden="true">
    <div className="catalog-card__media" />
    <div className="catalog-card__copy">
      <span />
      <span />
    </div>
  </article>
);

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(true);
  const { isWishlist, toggleWishlist } = useContext(CartContext);
  const queryString = searchParams.toString();

  const activeFilters = useMemo(
    () => ({
      gender: getValues(searchParams, "gender"),
      category: getValues(searchParams, "category"),
      size: getValues(searchParams, "size"),
      color: getValues(searchParams, "color"),
      material: getValues(searchParams, "material"),
    }),
    [searchParams]
  );

  const searchTerm = (searchParams.get("search") || "").trim();
  const fit = (searchParams.get("fit") || "").trim().toLowerCase();
  const tag = (searchParams.get("tag") || "").trim().toLowerCase();
  const wishlistOnly = searchParams.get("wishlist") === "true";
  const sort = searchParams.get("sort") || "featured";

  const filteredProducts = useMemo(() => {
    if (!Array.isArray(productData)) return [];

    const normalizedSearch = searchTerm.toLowerCase();
    const matches = productData.filter((product) => {
      const searchableText = [
        product.name,
        product.category,
        product.material,
        product.description,
        ...product.tags,
      ]
        .join(" ")
        .toLowerCase();
      const productColorText = product.colors.join(" ").toLowerCase();

      const searchMatches =
        !normalizedSearch ||
        searchableText.includes(normalizedSearch) ||
        (normalizedSearch === "new arrivals" && product.tags.includes("new"));

      return (
        searchMatches &&
        (!activeFilters.gender.length ||
          activeFilters.gender.includes(product.gender.toLowerCase())) &&
        (!activeFilters.category.length ||
          activeFilters.category.some((category) =>
            categoryMatches(product.category, category)
          )) &&
        (!activeFilters.size.length ||
          product.size.some((size) =>
            activeFilters.size.includes(size.toLowerCase())
          )) &&
        (!activeFilters.color.length ||
          activeFilters.color.some((color) =>
            colorPatterns[color]?.test(productColorText)
          )) &&
        (!activeFilters.material.length ||
          activeFilters.material.some((material) =>
            product.material.toLowerCase().includes(material)
          )) &&
        (!fit || product.attributes.Fit.toLowerCase().includes(fit)) &&
        (!tag || product.tags.includes(tag)) &&
        (!wishlistOnly || isWishlist(product.id))
      );
    });

    return [...matches].sort((first, second) => {
      if (sort === "price-asc") return first.price - second.price;
      if (sort === "price-desc") return second.price - first.price;
      if (sort === "newest") {
        return Number(second.tags.includes("new")) - Number(first.tags.includes("new"));
      }
      if (sort === "bestseller") {
        const tagDifference =
          Number(second.tags.includes("bestseller")) -
          Number(first.tags.includes("bestseller"));
        return tagDifference || second.reviews - first.reviews;
      }
      return first.id - second.id;
    });
  }, [activeFilters, fit, isWishlist, searchTerm, sort, tag, wishlistOnly]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setIsLoading(true);
    const loadingTimer = window.setTimeout(() => setIsLoading(false), 180);
    return () => window.clearTimeout(loadingTimer);
  }, [queryString]);

  const heading = useMemo(() => {
    if (wishlistOnly) return "Wishlist";
    if (searchTerm) return `Search: “${searchTerm}”`;
    if (activeFilters.category.length === 1) {
      return titleCase(activeFilters.category[0]);
    }
    if (activeFilters.gender.length === 1) {
      return `${titleCase(activeFilters.gender[0])}'s collection`;
    }
    return "Shop all";
  }, [activeFilters.category, activeFilters.gender, searchTerm, wishlistOnly]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const previousTitle = document.title;
    document.title = `${heading} | ${siteConfig.brandName}`;
    return () => {
      document.title = previousTitle;
    };
  }, [heading]);

  const toggleFilter = (key, value) => {
    const nextParams = new URLSearchParams(searchParams);
    const values = getValues(nextParams, key);
    const nextValues = values.includes(value)
      ? values.filter((item) => item !== value)
      : [...values, value];

    if (nextValues.length) nextParams.set(key, nextValues.join(","));
    else nextParams.delete(key);

    setSearchParams(nextParams, { replace: true });
  };

  const updateSort = (event) => {
    const nextParams = new URLSearchParams(searchParams);
    if (event.target.value === "featured") nextParams.delete("sort");
    else nextParams.set("sort", event.target.value);
    setSearchParams(nextParams, { replace: true });
  };

  const clearFilters = () => {
    const nextParams = new URLSearchParams();
    if (sort !== "featured") nextParams.set("sort", sort);
    setSearchParams(nextParams, { replace: true });
  };

  const removeSingleFilter = (key, value) => {
    if (["search", "fit", "tag", "wishlist"].includes(key)) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete(key);
      setSearchParams(nextParams, { replace: true });
      return;
    }
    toggleFilter(key, value);
  };

  const chipFilters = [
    ...Object.entries(activeFilters).flatMap(([key, values]) =>
      values.map((value) => ({ key, value, label: titleCase(value) }))
    ),
    ...(searchTerm ? [{ key: "search", value: searchTerm, label: `“${searchTerm}”` }] : []),
    ...(fit ? [{ key: "fit", value: fit, label: `Fit: ${titleCase(fit)}` }] : []),
    ...(tag ? [{ key: "tag", value: tag, label: titleCase(tag) }] : []),
    ...(wishlistOnly ? [{ key: "wishlist", value: "true", label: "Wishlist" }] : []),
  ];

  const visibleProducts = filteredProducts.slice(0, visibleCount);

  return (
    <main className="aa-shop" id="main-content">
      <header className="catalog-header">
        <span>RESPONSIBLE EVERYDAY STYLES</span>
        <h1>{heading}</h1>
        <p>
          Considered fits, more responsible materials and pieces designed to be
          worn on repeat.
        </p>
      </header>

      <section className="catalog-toolbar" aria-label="Product filters and sorting">
        <div className="catalog-toolbar__filter-label">
          <HiOutlineAdjustmentsHorizontal aria-hidden="true" />
          Filters
          {chipFilters.length > 0 && <span>{chipFilters.length}</span>}
        </div>
        <FilterDropdown
          label="Gender"
          options={[
            { value: "women", label: "Women" },
            { value: "men", label: "Men" },
          ]}
          selectedValues={activeFilters.gender}
          onToggle={(value) => toggleFilter("gender", value)}
        />
        <FilterDropdown
          label="Category"
          options={categoryOptions}
          selectedValues={activeFilters.category}
          onToggle={(value) => toggleFilter("category", value)}
        />
        <FilterDropdown
          label="Size"
          options={sizeOptions}
          selectedValues={activeFilters.size}
          onToggle={(value) => toggleFilter("size", value)}
        />
        <FilterDropdown
          label="Color"
          options={colorOptions}
          selectedValues={activeFilters.color}
          onToggle={(value) => toggleFilter("color", value)}
          color
        />
        <FilterDropdown
          label="Material"
          options={materialOptions}
          selectedValues={activeFilters.material}
          onToggle={(value) => toggleFilter("material", value)}
        />

        <label className="catalog-sort">
          <span>Sort by</span>
          <select value={sort} onChange={updateSort} aria-label="Sort products">
            <option value="featured">Featured</option>
            <option value="newest">Newest</option>
            <option value="bestseller">Bestsellers</option>
            <option value="price-asc">Price, low to high</option>
            <option value="price-desc">Price, high to low</option>
          </select>
          <HiChevronDown aria-hidden="true" />
        </label>
      </section>

      <div className="catalog-status">
        <span aria-live="polite">
          {isLoading ? "Loading products…" : `${filteredProducts.length} products`}
        </span>
        {chipFilters.length > 0 && (
          <button type="button" onClick={clearFilters}>
            Clear all
          </button>
        )}
      </div>

      {chipFilters.length > 0 && (
        <div className="catalog-chips" aria-label="Active filters">
          {chipFilters.map((filter) => (
            <button
              type="button"
              key={`${filter.key}-${filter.value}`}
              onClick={() => removeSingleFilter(filter.key, filter.value)}
            >
              {filter.label}
              <HiOutlineXMark aria-hidden="true" />
            </button>
          ))}
        </div>
      )}

      {!Array.isArray(productData) ? (
        <section className="catalog-empty" role="alert">
          <h2>We could not load the collection.</h2>
          <p>Please refresh the page or try again in a moment.</p>
        </section>
      ) : isLoading ? (
        <section className="catalog-grid" aria-label="Loading products">
          {Array.from({ length: 8 }, (_, index) => (
            <CatalogSkeleton key={index} />
          ))}
        </section>
      ) : visibleProducts.length > 0 ? (
        <>
          <section className="catalog-grid" aria-label="Products">
            {visibleProducts.map((product) => (
              <CatalogCard
                key={product.id}
                product={product}
                favorite={isWishlist(product.id)}
                onToggleWishlist={toggleWishlist}
              />
            ))}
          </section>
          {visibleCount < filteredProducts.length && (
            <div className="catalog-load-more">
              <span>
                Showing {visibleProducts.length} of {filteredProducts.length}
              </span>
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              >
                Load more
              </button>
            </div>
          )}
        </>
      ) : (
        <section className="catalog-empty">
          <h2>No pieces match your selection.</h2>
          <p>Try removing a filter or explore the full collection.</p>
          <button type="button" onClick={clearFilters}>
            View all products
          </button>
        </section>
      )}
    </main>
  );
}

export default Shop;
